import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { analyticsAPI } from "../api";
import {
  ArrowLeft, Star, User, Bot, Clock, MessageSquare,
  AlertTriangle, CheckCircle2, TrendingUp, ExternalLink,
} from "lucide-react";
import { motion } from "framer-motion";

function StarRating({ score, max = 5 }) {
  return (
    <div className="flex items-center space-x-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`w-4 h-4 ${i <= score ? "text-amber-400 fill-amber-400" : "text-gray-200"}`}
        />
      ))}
      <span className="ml-1 text-sm font-medium text-gray-600">{score?.toFixed(1) || "0.0"}/{max}</span>
    </div>
  );
}

function ScoreBadge({ score }) {
  if (!score) return null;
  const color = score >= 4 ? "bg-green-100 text-green-700" : score >= 3 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700";
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${color}`}>{score.toFixed(1)}</span>;
}

const sentimentColors = {
  positive: { bg: "bg-green-50", text: "text-green-700", label: "Tích cực" },
  negative: { bg: "bg-red-50", text: "text-red-700", label: "Tiêu cực" },
  neutral: { bg: "bg-gray-50", text: "text-gray-600", label: "Trung lập" },
};

export default function ConversationDetailPage() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("messages");

  useEffect(() => {
    fetchDetail();
  }, [conversationId]);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const res = await analyticsAPI.conversationDetail(conversationId);
      setData(res.data);
    } catch (err) {
      console.error("Conv detail error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    // If navigated from employee page, go back there
    if (location.state?.from) {
      navigate(location.state.from);
    } else {
      navigate("/conversations");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-16">
        <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">Không tìm thấy hội thoại</p>
        <button onClick={handleBack} className="mt-4 text-primary-600 hover:underline text-sm">← Quay lại</button>
      </div>
    );
  }

  const conv = data.conversation;
  const messages = data.messages || [];
  const aiEval = data.ai_evaluation;
  const agentEvals = data.agent_evaluations || [];
  const sentiment = sentimentColors[conv.sentiment_label] || sentimentColors.neutral;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
        <div className="flex items-start space-x-3 sm:space-x-4 min-w-0">
          <button onClick={handleBack} className="p-2 hover:bg-gray-100 rounded-lg transition flex-shrink-0 mt-0.5" aria-label="Quay lại">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex items-start space-x-3 min-w-0">
            {conv.customer_avatar ? (
              <img src={conv.customer_avatar} alt="" className="w-10 h-10 rounded-full flex-shrink-0 mt-0.5" />
            ) : (
              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <User className="w-5 h-5 text-primary-600" />
              </div>
            )}
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-gray-900 truncate">{conv.customer_name}</h1>
              <div className="flex flex-wrap items-center gap-1.5 mt-1">
                <span className={`text-[10px] sm:text-xs px-2 py-0.5 rounded-full font-medium ${
                  conv.status === "resolved" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
                }`}>
                  {conv.status === "resolved" ? "Đã xử lý" : "Đang mở"}
                </span>
                <span className={`text-[10px] sm:text-xs px-2 py-0.5 rounded-full ${sentiment.bg} ${sentiment.text}`}>
                  {sentiment.label}
                </span>
                {conv.topic && (
                  <span className="text-[10px] sm:text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{conv.topic}</span>
                )}
                {conv.is_return_visit && (
                  <span className="text-[10px] sm:text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">Khách quay lại</span>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="text-left sm:text-right text-xs text-gray-500 flex sm:flex-col items-center sm:items-end gap-3 sm:gap-0 pl-11 sm:pl-0 border-t sm:border-t-0 pt-2 sm:pt-0">
          <p className="font-semibold text-gray-700 sm:font-normal sm:text-gray-500">Conv #{conv.id}</p>
          <p>{conv.started_at ? new Date(conv.started_at).toLocaleString("vi-VN") : ""}</p>
          <p>{conv.message_count} tin nhắn</p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border p-3 shadow-sm">
          <p className="text-xs text-gray-500 uppercase">Thời gian PH</p>
          <p className="text-lg font-bold text-gray-900">
            {conv.first_response_time_sec ? `${Math.round(conv.first_response_time_sec / 60)}p` : "—"}
          </p>
        </div>
        <div className="bg-white rounded-xl border p-3 shadow-sm">
          <p className="text-xs text-gray-500 uppercase">Thời gian GQ</p>
          <p className="text-lg font-bold text-gray-900">
            {conv.resolution_time_sec ? `${Math.round(conv.resolution_time_sec / 3600)}h` : "—"}
          </p>
        </div>
        {aiEval && (
          <>
            <div className="bg-white rounded-xl border p-3 shadow-sm">
              <p className="text-xs text-gray-500 uppercase">Hài lòng</p>
              <div className="mt-1"><StarRating score={aiEval.satisfaction_score} /></div>
            </div>
            <div className="bg-white rounded-xl border p-3 shadow-sm">
              <p className="text-xs text-gray-500 uppercase">Chất lượng GQ</p>
              <div className="mt-1"><StarRating score={aiEval.resolution_quality_score} /></div>
            </div>
          </>
        )}
        {!aiEval && (
          <>
            <div className="bg-white rounded-xl border p-3 shadow-sm">
              <p className="text-xs text-gray-500 uppercase">Hài lòng</p>
              <p className="text-sm text-gray-400 mt-1">Chưa đánh giá</p>
            </div>
            <div className="bg-white rounded-xl border p-3 shadow-sm">
              <p className="text-xs text-gray-500 uppercase">Chất lượng</p>
              <p className="text-sm text-gray-400 mt-1">Chưa đánh giá</p>
            </div>
          </>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
        {[
          { key: "messages", label: "Tin nhắn", count: messages.length },
          { key: "evaluation", label: "Đánh giá AI", count: agentEvals.length },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition ${
              activeTab === tab.key
                ? "bg-white text-primary-700 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label} {tab.count > 0 && <span className="ml-1 text-xs">({tab.count})</span>}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "messages" ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-white rounded-xl border shadow-sm"
        >
          {/* Summary */}
          {conv.summary && (
            <div className="p-4 border-b bg-blue-50">
              <p className="text-xs font-medium text-blue-700 uppercase mb-1">Tóm tắt AI</p>
              <p className="text-sm text-blue-900">{conv.summary}</p>
            </div>
          )}

          {/* Messages */}
          <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto">
            {messages.length === 0 ? (
              <p className="text-center text-gray-400 py-8">Không có tin nhắn</p>
            ) : (
              messages.map((msg, i) => {
                const isAgent = msg.sender_type === "oa" || msg.sender_type === "agent";
                return (
                  <motion.div
                    key={msg.id || i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.02 }}
                    className={`flex ${isAgent ? "justify-end" : "justify-start"}`}
                  >
                    <div className={`max-w-[88%] sm:max-w-[75%] ${isAgent ? "order-1" : ""}`}>
                      {/* Sender label */}
                      <p className={`text-xs mb-1 ${isAgent ? "text-right text-primary-500" : "text-gray-400"}`}>
                        {isAgent ? (msg.agent_name || "Nhân viên") : conv.customer_name}
                      </p>
                      <div
                        className={`px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-2xl text-sm ${
                          isAgent
                            ? "bg-primary-600 text-white rounded-br-md"
                            : "bg-gray-100 text-gray-800 rounded-bl-md"
                        }`}
                      >
                        <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                      </div>
                      <div className={`flex items-center space-x-2 mt-1 ${isAgent ? "justify-end" : ""}`}>
                        <span className="text-xs text-gray-400">
                          {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" }) : ""}
                        </span>
                        {msg.sentiment && (
                          <span className={`text-xs ${
                            msg.sentiment === "positive" ? "text-green-500" : msg.sentiment === "negative" ? "text-red-500" : "text-gray-400"
                          }`}>
                            {msg.sentiment === "positive" ? "😊" : msg.sentiment === "negative" ? "😞" : ""}
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>
        </motion.div>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
          {/* Overall AI Evaluation */}
          {aiEval && (
            <div className="bg-white rounded-xl border shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-gray-900">Đánh giá tổng thể AI</h3>
                <span className="text-xs text-gray-400">
                  {aiEval.evaluated_at ? new Date(aiEval.evaluated_at).toLocaleString("vi-VN") : ""}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Hài lòng KH</p>
                  <StarRating score={aiEval.satisfaction_score} />
                  <p className="text-xs text-gray-600 mt-2 italic">{aiEval.satisfaction_reason}</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Thái độ NV</p>
                  <StarRating score={aiEval.agent_attitude_score} />
                  <p className="text-xs text-gray-600 mt-2 italic">{aiEval.agent_attitude_reason}</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Chất lượng GQ</p>
                  <StarRating score={aiEval.resolution_quality_score} />
                  <p className="text-xs text-gray-600 mt-2 italic">{aiEval.resolution_quality_reason}</p>
                </div>
              </div>
              {aiEval.improvement_categories?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {aiEval.improvement_categories.map((c, i) => (
                    <span key={i} className="text-xs bg-red-50 text-red-700 px-2 py-0.5 rounded-full">{c}</span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Per-Agent Evaluations */}
          {agentEvals.length > 0 && (
            <div className="bg-white rounded-xl border shadow-sm p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-4">
                Đánh giá theo nhân viên ({agentEvals.length})
              </h3>
              <div className="space-y-4">
                {agentEvals.map((ev) => (
                  <div key={ev.id} className="p-4 rounded-lg border border-gray-100 hover:border-gray-200 transition">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                          <span className="text-primary-700 font-bold text-sm">{(ev.agent_name || "?").charAt(0)}</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {ev.agent_name || `Agent #${ev.agent_id}`}
                          </p>
                          <button
                            onClick={() => navigate(`/employees/${ev.agent_id}`)}
                            className="text-xs text-primary-600 hover:underline flex items-center"
                          >
                            Xem profile <ExternalLink className="w-3 h-3 ml-1" />
                          </button>
                        </div>
                      </div>
                      <span className="text-xs text-gray-400">
                        {ev.evaluated_at ? new Date(ev.evaluated_at).toLocaleString("vi-VN") : ""}
                      </span>
                    </div>

                    <div className="grid grid-cols-3 gap-3 mb-3">
                      <div>
                        <p className="text-xs text-gray-500">Hài lòng</p>
                        <StarRating score={ev.satisfaction_score} />
                        <p className="text-xs text-gray-500 mt-1 italic line-clamp-2">{ev.satisfaction_reason}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Thái độ</p>
                        <StarRating score={ev.agent_attitude_score} />
                        <p className="text-xs text-gray-500 mt-1 italic line-clamp-2">{ev.agent_attitude_reason}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Chất lượng</p>
                        <StarRating score={ev.resolution_quality_score} />
                        <p className="text-xs text-gray-500 mt-1 italic line-clamp-2">{ev.resolution_quality_reason}</p>
                      </div>
                    </div>

                    {ev.summary && (
                      <p className="text-sm text-gray-700 bg-gray-50 p-2 rounded mb-2">{ev.summary}</p>
                    )}

                    {ev.improvement_categories?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {ev.improvement_categories.map((c, i) => (
                          <span key={i} className="text-xs bg-red-50 text-red-700 px-2 py-0.5 rounded-full">{c}</span>
                        ))}
                      </div>
                    )}

                    {ev.improvement_suggestions?.length > 0 && (
                      <div className="space-y-1">
                        {ev.improvement_suggestions.map((s, i) => (
                          <p key={i} className="text-xs text-amber-700">💡 {s}</p>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!aiEval && agentEvals.length === 0 && (
            <div className="bg-white rounded-xl border shadow-sm p-8 text-center">
              <AlertTriangle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Cuộc hội thoại này chưa được AI đánh giá</p>
              <p className="text-xs text-gray-400 mt-1">Hệ thống đánh giá tự động mỗi 30 phút</p>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}
